// Customer tab "..." overflow menu (FR-67; prototype order-feedback-landing-
// page v83 `.cdm-*`): "View profile" + "Restrict this customer". Replaces the
// inline View-profile link on RecoveryAI cases.
//
// A hand-rolled WAI-ARIA menu-button, mirroring RecoveryAI/CaseDetail/
// CaseActionsMenu.tsx's documented exemption from the "uikit Button for
// every clickable" convention (uikit's Button can't carry `role="menu"`/
// `role="menuitem"`/`aria-haspopup`/`aria-controls`). See that file's header.
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useStyletron } from 'styletron-react'

const t = buildTranslate('feedback_inbox.rail.customer')

const MENU_ID = 'feedback-inbox-customer-actions-menu'
const DISABLED_REASON_ID = `${MENU_ID}-restrict-disabled-reason`
const RESTRICT_TOOLTIP_ID = `${MENU_ID}-restrict-tooltip`
const TRIGGER_SIZE = 32
const MENU_MIN_WIDTH = 220

export interface CustomerActionsMenuProps {
  // Omitted when the viewer can't open the profile (no uid / no access).
  profileTo: string | null
  // Set when Restrict is disabled (no permission / already restricted).
  restrictDisabledReason: string | undefined
  onRestrictClick: () => void
}

const CustomerActionsMenu = ({
  profileTo,
  restrictDisabledReason,
  onRestrictClick,
}: CustomerActionsMenuProps) => {
  const [css] = useStyletron()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const restrictDisabled = restrictDisabledReason !== undefined

  const closeMenu = () => {
    setOpen(false)
    // Return focus to the trigger so a keyboard user isn't stranded on a
    // hidden element after Escape / outside-click.
    triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!open) return undefined
    const onDocClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) closeMenu()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // Focus the first menuitem on open so the arrow-key handler below has
  // somewhere to move from.
  useEffect(() => {
    if (!open) return
    const firstItem =
      menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')
    firstItem?.focus()
  }, [open])

  // WAI-ARIA menu keyboard nav: Arrow keys move between menuitems (wrapping
  // at the boundaries), Home/End jump to the first/last.
  const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
    event.preventDefault()
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []
    )
    if (items.length === 0) return
    const currentIndex = items.indexOf(document.activeElement as HTMLElement)
    let nextIndex = currentIndex
    if (event.key === 'ArrowDown') {
      nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
    } else if (event.key === 'ArrowUp') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = items.length - 1
    }
    items[nextIndex]?.focus()
  }

  // aria-disabled (not native `disabled`) keeps the item focusable so a
  // keyboard user can still reach the disabled reason.
  const handleRestrictClick = () => {
    if (restrictDisabled) return
    setOpen(false)
    onRestrictClick()
  }

  const itemStyle = {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    textAlign: 'left' as const,
    background: 'none',
    border: 'none',
    fontSize: '14px',
    lineHeight: 1.3,
    textDecoration: 'none',
    boxSizing: 'border-box' as const,
  }

  const restrictItem = (
    <button
      type="button"
      role="menuitem"
      aria-disabled={restrictDisabled || undefined}
      aria-describedby={restrictDisabled ? DISABLED_REASON_ID : undefined}
      onClick={handleRestrictClick}
      className={css({
        ...itemStyle,
        color: restrictDisabled ? palette.grey50 : palette.cayenne50,
        cursor: restrictDisabled ? 'not-allowed' : 'pointer',
        ':hover': restrictDisabled ? {} : { backgroundColor: palette.grey05 },
      })}
    >
      {t('restrict')}
    </button>
  )

  return (
    <div
      ref={containerRef}
      className={css({ position: 'relative', display: 'inline-flex' })}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={t('actions_menu_aria')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={MENU_ID}
        onClick={() => setOpen(prev => !prev)}
        className={css({
          width: `${TRIGGER_SIZE}px`,
          height: `${TRIGGER_SIZE}px`,
          // Keeps the 32px trigger from growing the heading row.
          margin: '-8px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: open ? palette.grey20 : 'transparent',
          border: 'none',
          borderRadius: '6px',
          color: palette.grey70,
          cursor: 'pointer',
          ':hover': { background: palette.grey10 },
        })}
      >
        <i className="fa fa-ellipsis-h" aria-hidden="true" />
      </button>
      {open && (
        <div
          id={MENU_ID}
          ref={menuRef}
          role="menu"
          aria-label={t('actions_menu_aria')}
          onKeyDown={onMenuKeyDown}
          className={css({
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            minWidth: `${MENU_MIN_WIDTH}px`,
            backgroundColor: palette.white,
            border: `1px solid ${palette.grey30}`,
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            zIndex: 10,
          })}
        >
          {profileTo && (
            <Link
              to={profileTo}
              role="menuitem"
              className={css({
                ...itemStyle,
                color: palette.grey90,
                ':hover': {
                  backgroundColor: palette.grey05,
                  color: palette.grey90,
                  textDecoration: 'none',
                },
              })}
            >
              {t('view_profile')}
            </Link>
          )}
          {restrictDisabled ? (
            // PermissionTooltip's hover+focus trigger, put straight on the
            // menuitem: its wrapper span would be a second tab stop inside
            // role="menu", and this item is already focusable (aria-disabled).
            <OverlayTrigger
              placement="left"
              trigger={['hover', 'focus']}
              overlay={
                <Tooltip id={RESTRICT_TOOLTIP_ID}>
                  {restrictDisabledReason}
                </Tooltip>
              }
            >
              {restrictItem}
            </OverlayTrigger>
          ) : (
            restrictItem
          )}
          {restrictDisabled && (
            <span id={DISABLED_REASON_ID} className="sr-only">
              {restrictDisabledReason}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default CustomerActionsMenu
